/**
 * 매장 내용 가져오기 (/my/import/ · page-partner-import.php).
 *
 * 흐름: 원본 매장 라디오(서버가 그린 목록 · 관리자 검색 결과) → admin-ajax(Partner_Import::AJAX_PREVIEW)로 원본 · 대상의
 * 항목별 요약을 받아 ② 카드에 그린다 → 항목 · 방식(바로 반영 · 초안)을 고르고 [가져오기] → 확인 모달(항목마다 "지금 값 → 원본")
 * → admin-ajax(Partner_Import::AJAX_APPLY). 바로 반영이면 이 화면에서 결과와 덧말을 보여 주고 요약을 다시 읽는다.
 * 초안이면 서버가 준 주소(/setup/)로 옮긴다 — 편집기가 "반영하지 않은 임시 저장본" 으로 연다.
 *
 * 초안 방식은 상품 · 케어 · 참고이미지를 담을 수 없어(서버 DRAFT_ITEMS 와 같은 규칙) 그 항목의 체크를 막는다.
 */

import { busy } from './busyButton';
import { lockModal } from './initModal';
import { postForm, reloadWith, setNotice } from './partnerForm';

/** 옮긴 화면(/setup/)에 띄울 알림을 맡겨 두는 자리 — initShopSetupEditor.js 가 같은 키를 본다 */
const FLASH_KEY = 'qrshop_setup_flash';

/** 초안 방식에서 뺄 항목 */
const NO_DRAFT = new Set(['products', 'guide']);

const el = (tag, className, text) => {
  const node = document.createElement(tag);

  if (className) {
    node.className = className;
  }

  if (undefined !== text) {
    node.textContent = text;
  }

  return node;
};

/** 항목 요약 한 칸 — 사용 스위치 · 개수 · 제목 몇 줄 · 덧말 */
const renderSummary = (summary, label) => {
  const col = el('div', 'import__col');
  col.append(el('span', 'import__col-label', label));

  const count = el('p', 'import__count');

  if (null !== summary.use && undefined !== summary.use) {
    const badge = el(
      'span',
      `badge ${summary.use ? 'badge--success' : ''}`,
      summary.use ? '사용' : '사용 안 함'
    );
    count.append(badge);
  }

  count.append(document.createTextNode(0 === summary.count ? '비어 있음' : `${summary.count}개`));
  col.append(count);

  if (summary.lines?.length) {
    const more =
      summary.count > summary.lines.length ? ` 외 ${summary.count - summary.lines.length}개` : '';
    col.append(el('p', 'import__lines', summary.lines.join(' · ') + more));
  }

  if (summary.note) {
    col.append(el('p', 'import__note', summary.note));
  }

  return col;
};

/** 원본 매장 라디오 카드 — 관리자 검색 결과용 (page-partner-import.php 의 카드와 같은 마크업) */
const renderSource = (shop, currentId) => {
  const li = document.createElement('li');
  const label = el('label', 'import__source');
  const input = document.createElement('input');
  const isCurrent = Number(shop.id) === currentId;
  const available = false !== shop.available && !isCurrent;

  input.type = 'radio';
  input.name = 'source_id';
  input.value = String(shop.id);
  input.disabled = !available;
  label.append(input);

  const body = el('span', 'import__source-body');
  const title = el('span', 'import__source-title', shop.title || `#${shop.id}`);

  if (isCurrent) {
    title.append(el('span', 'badge badge--brand', '현재 매장'));
  } else if (shop.own) {
    title.append(el('span', 'badge badge--info', '내 매장'));
  } else if (!available) {
    title.append(el('span', 'badge badge--warning', '사용 불가'));
  }

  body.append(title);

  if (shop.user_login) {
    body.append(el('span', 'import__source-meta', `아이디 ${shop.user_login}`));
  }

  if (shop.address) {
    body.append(el('span', 'import__source-meta', shop.address));
  }

  if (isCurrent) {
    body.append(el('span', 'import__source-meta', '지금 고른 매장은 원본이 될 수 없습니다.'));
  } else if (!available) {
    body.append(
      el('span', 'import__source-meta', shop.reason || '지금은 가져올 수 없는 매장입니다.')
    );
  }

  label.append(body);
  li.append(label);

  return li;
};

const InitPartnerImport = () => {
  const root = document.querySelector('[data-import]');

  if (!root) {
    return;
  }

  const status = root.querySelector('[data-import-status]');
  const notes = root.querySelector('[data-import-notes]');
  const itemsCard = root.querySelector('[data-import-items]');
  const list = root.querySelector('[data-import-list]');
  const compare = root.querySelector('[data-import-compare]');
  const modeCard = root.querySelector('[data-import-mode]');
  const actions = root.querySelector('[data-import-actions]');
  const openButton = root.querySelector('[data-import-open]');
  const checkAll = root.querySelector('[data-import-check-all]');
  const dialog = document.getElementById('import-confirm');
  const confirmLead = dialog?.querySelector('[data-import-confirm-lead]');
  const confirmList = dialog?.querySelector('[data-import-confirm-list]');
  const confirmStatus = dialog?.querySelector('[data-import-confirm-status]');
  const confirmButton = dialog?.querySelector('[data-import-confirm]');
  const targetTitle = root.dataset.targetTitle || '이';

  let preview = null;
  let sourceId = 0;

  const mode = () => root.querySelector('input[name="mode"]:checked')?.value || 'apply';
  const itemInputs = () => Array.from(list.querySelectorAll('input[name="items[]"]'));
  const checkedItems = () => itemInputs().filter((input) => input.checked && !input.disabled);

  /** 초안 방식에서는 상품 · 케어 · 참고이미지 체크를 막는다 */
  const syncMode = () => {
    const draft = 'draft' === mode();

    itemInputs().forEach((input) => {
      const item = input.closest('[data-item]');
      const blocked = draft && NO_DRAFT.has(input.value);

      input.disabled = blocked;
      item?.classList.toggle('import__item--off', blocked);

      const hint = item?.querySelector('[data-item-draft-hint]');

      if (hint) {
        hint.hidden = !blocked;
      }
    });

    const photos = list.querySelector('input[name="photos"]');

    if (photos) {
      photos.disabled = draft;
      photos.closest('[data-item-photos]')?.classList.toggle('import__item--off', draft);
    }

    openButton.disabled = 0 === checkedItems().length;

    if (checkAll) {
      const inputs = itemInputs().filter((input) => !input.disabled);

      checkAll.textContent =
        inputs.length && inputs.every((input) => input.checked) ? '전체 해제' : '전체 선택';
    }
  };

  /** 참고이미지 체크는 가격 정보 체크를 따라간다 — 그 뒤 따로 끄고 켤 수 있다 */
  const syncPhotos = () => {
    const price = itemInputs().find((input) => 'price' === input.value);
    const photos = list.querySelector('input[name="photos"]');

    if (price && photos && !photos.disabled) {
      photos.checked = price.checked;
    }
  };

  /** ② 카드 — 항목마다 체크 + 원본 · 지금 값 */
  const renderItems = () => {
    list.replaceChildren();

    Object.entries(preview.items).forEach(([key, item]) => {
      const li = el('li', 'import__item');
      li.dataset.item = key;

      const check = el('label', 'choice import__check');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = 'items[]';
      input.value = key;
      // 처음에는 아무것도 고르지 않는다 — 덮어쓸 항목은 원장이 직접 고른다
      input.checked = false;
      check.append(input, document.createTextNode(item.label));
      li.append(check);

      const cols = el('div', 'import__cols');
      cols.append(renderSummary(item.source, `원본 · ${preview.source.title}`));
      cols.append(renderSummary(item.target, `지금 값 · ${preview.target.title || targetTitle}`));
      li.append(cols);

      if ('price' === key) {
        const option = el('label', 'choice import__option');
        option.dataset.itemPhotos = '';
        const photos = document.createElement('input');
        photos.type = 'checkbox';
        photos.name = 'photos';
        photos.checked = false;
        option.append(
          photos,
          document.createTextNode('참고이미지도 복사 (파일을 이 매장 것으로 새로 만듭니다)')
        );
        li.append(option);
      }

      if (NO_DRAFT.has(key)) {
        const hint = el(
          'p',
          'import__hint',
          '초안 방식에서는 가져올 수 없습니다. 바로 반영을 고르세요.'
        );
        hint.dataset.itemDraftHint = '';
        hint.hidden = true;
        li.append(hint);
      }

      list.append(li);
    });

    compare.textContent = `${preview.source.title} → ${preview.target.title || targetTitle}\n고른 항목의 지금 값은 원본 값으로 통째로 바뀝니다. 항목마다 원본과 지금 값을 견주어 보고 고르세요.`;
    itemsCard.hidden = false;
    modeCard.hidden = false;
    actions.hidden = false;
    syncMode();
  };

  /** 원본을 고르면 요약을 읽는다 */
  const loadPreview = async (id) => {
    sourceId = Number(id) || 0;
    preview = null;
    itemsCard.hidden = true;
    modeCard.hidden = true;
    actions.hidden = true;
    notes.hidden = true;

    if (sourceId <= 0) {
      return;
    }

    setNotice(status, '원본 매장의 내용을 읽는 중입니다...', 'warning');

    try {
      const data = await postForm(root, root.dataset.previewAction, { source_id: sourceId });

      preview = data;
      setNotice(status, '');
      renderItems();
      itemsCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      setNotice(status, error.message);
    }
  };

  root.addEventListener('change', (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) {
      return;
    }

    if ('source_id' === target.name && target.checked) {
      loadPreview(target.value);
      return;
    }

    if ('items[]' === target.name && 'price' === target.value) {
      syncPhotos();
    }

    if ('mode' === target.name || 'items[]' === target.name) {
      syncMode();
    }
  });

  checkAll?.addEventListener('click', () => {
    const inputs = itemInputs().filter((input) => !input.disabled);
    const all = inputs.every((input) => input.checked);

    inputs.forEach((input) => {
      input.checked = !all;
    });

    syncPhotos();
    syncMode();
  });

  // ── 확인 모달 ─────────────────────────────────────────────────────────
  const describe = (summary) => {
    if (0 === summary.count) {
      return '비어 있음';
    }

    const use =
      null === summary.use || undefined === summary.use
        ? ''
        : summary.use
          ? '사용 · '
          : '사용 안 함 · ';

    return `${use}${summary.count}개`;
  };

  openButton?.addEventListener('click', () => {
    if (!preview || !(dialog instanceof HTMLDialogElement)) {
      return;
    }

    const selected = checkedItems();

    if (0 === selected.length) {
      setNotice(status, '가져올 항목을 하나 이상 골라 주세요.');
      return;
    }

    const draft = 'draft' === mode();

    confirmLead.textContent = draft
      ? `${preview.source.title} 매장의 값을 ${preview.target.title || targetTitle} 매장의 편집 초안에 채웁니다. 매장 사이트는 아직 바뀌지 않습니다.`
      : `${preview.source.title} 매장의 값을 ${preview.target.title || targetTitle} 매장에 바로 반영합니다.`;

    confirmList.replaceChildren(
      ...selected.map((input) => {
        const item = preview.items[input.value];
        const li = el('li', 'import__confirm-item');
        li.append(el('span', 'import__confirm-title', item.label));
        li.append(
          el(
            'p',
            'import__confirm-change',
            `지금 ${describe(item.target)} → 원본 ${describe(item.source)}`
          )
        );

        if ('price' === input.value) {
          const photos = list.querySelector('input[name="photos"]');
          li.append(
            el(
              'p',
              'import__confirm-change',
              !draft && photos?.checked
                ? '참고이미지도 복사합니다.'
                : '참고이미지는 지금 것을 그대로 둡니다.'
            )
          );
        }

        return li;
      })
    );

    dialog.querySelector('[data-import-confirm-alert]').hidden = draft;
    setNotice(confirmStatus, '');
    dialog.showModal();
  });

  confirmButton?.addEventListener('click', async () => {
    if (!preview || !(dialog instanceof HTMLDialogElement)) {
      return;
    }

    const selected = checkedItems();
    const draft = 'draft' === mode();
    const photos = list.querySelector('input[name="photos"]');
    const payload = new FormData();

    payload.set('source_id', String(sourceId));
    payload.set('mode', draft ? 'draft' : 'apply');
    payload.set('photos', !draft && photos?.checked ? '1' : '0');
    selected.forEach((input) => payload.append('items[]', input.value));

    const unlock = lockModal(dialog, confirmButton, '가져오는 중…');
    setNotice(confirmStatus, '사진 · 케어가 많으면 시간이 걸립니다. 창을 닫지 마세요.', 'warning');

    try {
      const data = await postForm(root, root.dataset.applyAction, payload);

      if ('draft' === data.mode) {
        reloadWith(FLASH_KEY, data.message || '초안에 채웠습니다.', data.redirect || '');
        return;
      }

      unlock();
      dialog.close();
      setNotice(status, data.message || '가져왔습니다.', 'success');

      const noteRows = Object.values(data.notes || {});
      notes.replaceChildren(...noteRows.map((note) => el('li', '', note)));
      notes.hidden = 0 === noteRows.length;

      // 지금 값이 바뀌었으니 요약을 다시 읽는다 — 안내는 그대로 둔다
      try {
        preview = await postForm(root, root.dataset.previewAction, { source_id: sourceId });
        renderItems();
      } catch (error) {
        // 요약을 다시 못 읽어도 가져오기는 끝났다 — 결과 안내만 남긴다
      }

      status.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (error) {
      unlock();
      setNotice(confirmStatus, error.message);
    }
  });

  // ── 관리자 검색 ─────────────────────────────────────────────────────────
  const form = root.querySelector('[data-import-search]');
  const results = root.querySelector('[data-import-results]');
  const resultLabel = root.querySelector('[data-import-result-label]');

  if (!form || !results) {
    return;
  }

  const input = form.elements.namedItem('q');
  const submit = form.querySelector('[data-import-search-submit]');
  const currentId = Number(root.dataset.current || 0);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const q = input.value.trim();

    if ('' === q) {
      setNotice(status, '검색어를 입력해 주세요.');
      input.focus();
      return;
    }

    const release = busy(submit, '찾는 중…');
    setNotice(status, '');

    try {
      const data = await postForm(root, root.dataset.searchAction, { q });
      const shops = Array.isArray(data.shops) ? data.shops : [];
      const targetId = preview?.target?.id || currentId;

      results.replaceChildren(...shops.map((shop) => renderSource(shop, targetId)));
      results.hidden = 0 === shops.length;

      if (resultLabel) {
        resultLabel.textContent =
          0 === shops.length
            ? `"${data.q || q}" 에 맞는 매장이 없습니다.`
            : `"${data.q || q}" 검색 결과 ${shops.length}개`;
        resultLabel.hidden = false;
      }
    } catch (error) {
      setNotice(status, error.message);
    } finally {
      release();
    }
  });
};

export default InitPartnerImport;
